export function toStandardNotation(num: number | string | undefined): string {
  // Handle undefined case
  if (num === undefined) {
    return "-";
  }

  // Handle string inputs
  if (typeof num === "string") {
    const trimmedNum = num.trim();

    // Special cases for strings
    if (trimmedNum.toLowerCase() === "nan") return "NaN";
    if (trimmedNum === "") return "-";

    const parsed = Number(trimmedNum);
    if (isNaN(parsed)) return num;

    num = parsed;
  }

  // Handle NaN
  if (typeof num === "number" && isNaN(num)) {
    return "NaN";
  }

  // Convert to number and get sign/absolute value
  const numberValue = num as number;
  const sign = numberValue < 0 ? "-" : "";
  const absValue = Math.abs(numberValue);

  // Handle zero
  if (absValue === 0) return "0";

  // Format based on the magnitude
  if (absValue >= 1000) {
    // Large numbers with K/M/B suffixes
    const suffixes = ["", "k", "M", "B"];
    let suffixIndex = 0;
    let scaledValue = absValue;

    while (suffixIndex < suffixes.length - 1) {
      if (scaledValue < 999.95) break;
      scaledValue /= 1000;
      suffixIndex++;
    }

    let formatted = scaledValue.toFixed(2);
    if (formatted.endsWith(".00")) {
      formatted = formatted.slice(0, -3);
    }

    return sign + formatted + suffixes[suffixIndex];
  } else if (absValue < 1) {
    // Small numbers with two significant digits
    if (absValue.toString().includes("e")) {
      // Handle very small numbers with scientific notation
      const numStr = absValue.toString();
      const match = numStr.match(/0\.0*/);
      if (match) {
        const zerosAfterDecimal = match[0].length - 2; // -2 for "0."
        return sign + absValue.toFixed(zerosAfterDecimal + 1);
      }
    }
    return sign + absValue.toPrecision(2);
  } else {
    // Numbers between 1 and 999
    let formatted = absValue.toFixed(2);
    if (formatted.endsWith(".00")) {
      formatted = formatted.slice(0, -3);
    }
    return sign + formatted;
  }
}

export function toMoneyNotation(prefix: string, value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1 || abs === 0) return `${prefix}${toStandardNotation(value)}`;
  if (abs < 0.005) return `<${prefix}0.01`;
  return `${value < 0 ? "-" : ""}${prefix}${abs.toFixed(2)}`;
}

export function toCommaNotation(num: number): string {
  return num.toLocaleString();
}
